import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { toast } from "sonner";
import FactorDialog from "@/components/hfacs/FactorDialog";
import { LEVEL_STYLES, LEVEL_ORDER, FACTOR_STATUS_STYLES } from "@/components/hfacs/hfacsLevels";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FactorsTab({ analysisId }) {
  const { user } = useAuth();
  const canDelete = user?.role === "Administrateur national" || user?.role === "Administrateur Site";
  const [referentiel, setReferentiel] = useState([]);
  const [selections, setSelections] = useState([]);
  const [facts, setFacts] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogFactor, setDialogFactor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ref, sel, fa] = await Promise.all([
        axios.get(`${API}/hfacs/reference`),
        axios.get(`${API}/hfacs/${analysisId}/factors`),
        axios.get(`${API}/hfacs/${analysisId}/facts`),
      ]);
      setReferentiel(ref.data);
      setSelections(sel.data);
      setFacts(fa.data);
    } catch (e) {
      toast.error("Impossible de charger le référentiel HFACS.");
    }
  }, [analysisId]);

  useEffect(() => { load(); }, [load]);

  const selectionByFactor = useMemo(() => {
    const map = {};
    selections.forEach((s) => { map[s.ID_FACTEUR] = s; });
    return map;
  }, [selections]);

  const grouped = useMemo(() => {
    const filtered = referentiel.filter((f) =>
      !search || f.LIBELLE?.toLowerCase().includes(search.toLowerCase()) || f.FAMILLE?.toLowerCase().includes(search.toLowerCase())
    );
    const byLevel = {};
    LEVEL_ORDER.forEach((lvl) => { byLevel[lvl] = {}; });
    filtered.forEach((f) => {
      if (!byLevel[f.NIVEAU]) byLevel[f.NIVEAU] = {};
      if (!byLevel[f.NIVEAU][f.FAMILLE]) byLevel[f.NIVEAU][f.FAMILLE] = [];
      byLevel[f.NIVEAU][f.FAMILLE].push(f);
    });
    return byLevel;
  }, [referentiel, search]);

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      const existing = selectionByFactor[payload.id_facteur];
      if (existing) {
        await axios.put(`${API}/hfacs/${analysisId}/factors/${existing.ID}`, payload);
      } else {
        await axios.post(`${API}/hfacs/${analysisId}/factors`, payload);
      }
      toast.success("Facteur enregistré.");
      setDialogFactor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement du facteur.");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSelection = async (selectionId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/hfacs/${analysisId}/factors/${selectionId}`);
      toast.success("Facteur retiré de l'analyse.");
      setDialogFactor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression du facteur.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="factors-search-input" placeholder="Rechercher un facteur ou une famille…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="space-y-8">
        {LEVEL_ORDER.map((level) => {
          const families = grouped[level] || {};
          if (Object.keys(families).length === 0) return null;
          const style = LEVEL_STYLES[level];
          return (
            <section key={level} data-testid={`factor-level-${level}`}>
              <h3 className={`inline-flex font-mono text-xs uppercase px-2.5 py-1 rounded-sm border mb-3 ${style.badge}`}>{level}</h3>
              {Object.entries(families).map(([famille, items]) => (
                <div key={famille} className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">{famille}</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((f) => {
                      const selection = selectionByFactor[f.ID_FACTEUR];
                      return (
                        <button
                          key={f.ID_FACTEUR}
                          data-testid={`factor-card-${f.ID_FACTEUR}`}
                          onClick={() => setDialogFactor(f)}
                          className={`text-left bg-card border ${style.border} rounded-md p-3 hover:-translate-y-0.5 transition-transform`}
                        >
                          <p className="text-sm font-medium leading-snug line-clamp-3">{f.LIBELLE}</p>
                          {selection && (
                            <span className={`inline-block mt-2 font-mono text-[10px] uppercase px-2 py-0.5 rounded-sm border ${FACTOR_STATUS_STYLES[selection.STATUT_ANALYSE] || ""}`}>
                              {selection.STATUT_ANALYSE}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          );
        })}
      </div>

      <FactorDialog
        open={!!dialogFactor}
        onOpenChange={(v) => !v && setDialogFactor(null)}
        factor={dialogFactor}
        selection={dialogFactor ? selectionByFactor[dialogFactor.ID_FACTEUR] : null}
        facts={facts}
        onSubmit={submit}
        submitting={submitting}
        onDelete={deleteSelection}
        deleting={deleting}
        canDelete={canDelete}
      />
    </div>
  );
}
